import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Lock, Save, ArrowLeft, Eye, RefreshCw, Megaphone, CalendarClock, Circle, BarChart3, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { PROMO_POPUP_DEFAULT } from '../config/promoPopup';
import { PromoPopupCard } from '../components/PromoPopupModal';
import { adminAnnouncementBarGet, adminAnnouncementBarSave } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Field spec drives form rendering AND admin help text. Keeping the
// contents adjacent to the schema makes future edits trivial.
const FIELDS = [
  { key: 'version',         label: 'Verzió (bumpold, ha akarod, hogy mindenki újra lássa)', type: 'text',   placeholder: 'nov-2026-free-consult' },
  { key: 'cooldown_days',   label: 'Bezárás utáni szünet (nap)',                            type: 'number', min: 0, max: 365 },
  { key: 'delay_ms',        label: 'Késleltetés megnyitás előtt (ms)',                      type: 'number', min: 0, max: 30000 },
  { key: 'brand',           label: 'Brand név',                                             type: 'text' },
  { key: 'brand_sub',       label: 'Brand alcím',                                           type: 'text' },
  { key: 'venue',           label: 'Helyszín (jobb felső sarok)',                           type: 'text' },
  { key: 'eyebrow',         label: 'Kis felirat („szemöldök")',                             type: 'text' },
  { key: 'headline_line1',  label: 'Nagy cím — 1. sor',                                     type: 'text' },
  { key: 'headline_line2',  label: 'Nagy cím — 2. sor',                                     type: 'text' },
  { key: 'headline_badge',  label: 'Arany badge (opcionális)',                              type: 'text' },
  { key: 'bullet_lead',     label: 'Pipa mögötti szöveg (első fele)',                       type: 'text' },
  { key: 'bullet_accent',   label: 'Pipa mögötti szöveg (arany rész)',                      type: 'text' },
  { key: 'bullet_sub',      label: 'Pipa alatti kiegészítés',                               type: 'textarea' },
  { key: 'discount_amount', label: 'Kedvezmény összege (nagy szöveg)',                      type: 'text' },
  { key: 'discount_sub',    label: 'Kedvezmény melletti magyarázat',                        type: 'text' },
  { key: 'discount_fine',   label: 'Kedvezmény alatti apró szöveg',                         type: 'textarea' },
  { key: 'cta_label',       label: 'CTA gomb felirata',                                     type: 'text' },
  { key: 'website',         label: 'Weboldal (bal alul)',                                   type: 'text' },
  { key: 'phone',           label: 'Telefonszám (jobb alul)',                               type: 'text' },
];

/**
 * Felső értesítő sáv szerkesztő — külön dokumentum, külön mentés. A lejárat
 * után a sáv magától eltűnik, de a dátum átírásával / a kapcsolóval bármikor
 * újra bekapcsolható.
 */
const AnnouncementBarEditor = ({ adminKey }) => {
  const [bar, setBar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAnnouncementBarGet(adminKey)
      .then((d) => setBar(d.bar))
      .catch(() => toast.error('A felső sáv betöltése sikertelen.'));
  }, [adminKey]);

  if (!bar) return null;

  const set = (k, v) => setBar((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const d = await adminAnnouncementBarSave(adminKey, {
        enabled: !!bar.enabled,
        version: bar.version || 'default',
        text: bar.text || '',
        text_mobile: bar.text_mobile || '',
        cta_label: bar.cta_label || '',
        cta_href: bar.cta_href || '',
        starts_at: bar.starts_at || '',
        ends_at: bar.ends_at || '',
      });
      setBar(d.bar);
      toast.success('Felső sáv mentve.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mentés sikertelen.');
    } finally {
      setSaving(false);
    }
  };

  const expired = bar.ends_at && new Date(bar.ends_at) < new Date();

  return (
    <div
      data-testid="admin-announcement-bar"
      className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 mb-6 space-y-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-bold text-white flex items-center gap-2">
          <Megaphone size={16} /> Felső értesítő sáv
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <Switch
              checked={!!bar.enabled}
              onCheckedChange={(v) => set('enabled', v)}
              data-testid="admin-announcement-enabled"
            />
            {bar.enabled ? 'Bekapcsolva' : 'Kikapcsolva'}
          </label>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            data-testid="admin-announcement-save"
            className="bg-yellow-600 hover:bg-yellow-500 text-gray-950 font-bold h-8 text-xs"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} className="mr-1" /> Sáv mentése</>}
          </Button>
        </div>
      </div>
      <p className="text-xs text-gray-400 -mt-1 leading-relaxed">
        A honlap legtetején, a menü fölött fut minden nyilvános oldalon. A „Látható eddig" után
        magától eltűnik — ha újra kellene, állíts be új dátumot (és bumpold a verziót, hogy azok is
        lássák, akik már bezárták).
        {expired && <span className="text-amber-400 font-bold"> Jelenleg lejárt, nem látszik.</span>}
      </p>
      <div>
        <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Szöveg</Label>
        <Textarea
          value={bar.text || ''}
          onChange={(e) => set('text', e.target.value)}
          rows={2}
          data-testid="admin-announcement-text"
          className="bg-gray-950 border-gray-700 text-white text-sm"
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">
          Rövid szöveg mobilra (768 px alatt)
        </Label>
        <Input
          value={bar.text_mobile || ''}
          onChange={(e) => set('text_mobile', e.target.value)}
          placeholder="Alapozó Program – 25 000 Ft-tól, csak 10 hely!"
          data-testid="admin-announcement-text_mobile"
          className="bg-gray-950 border-gray-700 text-white h-8 text-sm"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          { k: 'cta_label', label: 'Gomb felirata', ph: 'Megnézem →' },
          { k: 'cta_href', label: 'Gomb linkje', ph: '/alapozo' },
          { k: 'ends_at', label: 'Látható eddig (ISO)', ph: '2026-11-15T22:59:00+00:00' },
          { k: 'version', label: 'Verzió (bumpold új szövegnél)', ph: 'alapozo-2026-11' },
        ].map(({ k, label, ph }) => (
          <div key={k}>
            <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">{label}</Label>
            <Input
              value={bar[k] || ''}
              onChange={(e) => set(k, e.target.value)}
              placeholder={ph}
              data-testid={`admin-announcement-${k}`}
              className="bg-gray-950 border-gray-700 text-white h-8 text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const LoginScreen = ({ onAuth }) => {  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.get(`${API_BASE}/api/admin/promo-popup`, { headers: { 'X-Admin-Key': key.trim() } });
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
    } catch (_) { toast.error('Érvénytelen admin kulcs.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="admin-promo-login" className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <Megaphone className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Promó szerkesztő</h1>
        <p className="text-center text-sm text-gray-400 mb-6">Add meg az admin kulcsot a folytatáshoz.</p>
        <Input data-testid="admin-promo-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
        <Button type="submit" disabled={busy || !key.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
          {busy ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

const STATUS_TONES = {
  live:      { bg: 'bg-emerald-500/15', text: 'text-emerald-400',  dot: 'text-emerald-500' },
  scheduled: { bg: 'bg-sky-500/15',     text: 'text-sky-400',      dot: 'text-sky-500' },
  expired:   { bg: 'bg-red-500/15',     text: 'text-red-400',      dot: 'text-red-500' },
  off:       { bg: 'bg-gray-700/60',    text: 'text-gray-300',     dot: 'text-gray-400' },
};

const StatusPill = ({ status }) => {
  const t = STATUS_TONES[status.tone] || STATUS_TONES.off;
  return (
    <span
      data-testid={`admin-promo-status-${status.tone}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${t.bg} ${t.text}`}
    >
      <Circle className={`w-2 h-2 fill-current ${t.dot}`} strokeWidth={0} />
      {status.label}
    </span>
  );
};

const AdminPromoPopupPage = () => {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(() => ({ ...PROMO_POPUP_DEFAULT, variants: [] }));
  const [stats, setStats]       = useState([]);
  const [previewVariantKey, setPreviewVariantKey] = useState(null); // null = base

  const headers = useMemo(() => ({ 'X-Admin-Key': adminKey }), [adminKey]);

  // Compute the current live/scheduled/expired status from starts_at + ends_at.
  const status = useMemo(() => {
    const now = new Date();
    const parse = (v) => {
      if (!v) return null;
      const d = new Date(v.length === 16 ? v + ':00' : v);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const starts = parse(form.starts_at);
    const ends   = parse(form.ends_at);
    if (!form.enabled) return { tone: 'off',       label: 'Kikapcsolva' };
    if (starts && now < starts) return { tone: 'scheduled', label: `Indul: ${starts.toLocaleString('hu-HU')}` };
    if (ends   && now > ends)   return { tone: 'expired',   label: `Lejárt: ${ends.toLocaleString('hu-HU')}` };
    if (ends) return { tone: 'live', label: `Aktív — lejár: ${ends.toLocaleString('hu-HU')}` };
    return { tone: 'live', label: 'Aktív most' };
  }, [form.enabled, form.starts_at, form.ends_at]);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const [{ data: promoRes }, { data: statsRes }] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/promo-popup`, { headers }),
        axios.get(`${API_BASE}/api/admin/promo-popup/stats`, { headers }).catch(() => ({ data: { rows: [] } })),
      ]);
      const promoDoc = promoRes?.promo || {};
      const merged = {
        ...PROMO_POPUP_DEFAULT,
        ...promoDoc,
        variants: Array.isArray(promoDoc.variants) ? promoDoc.variants : [],
      };
      setForm(merged);
      setStats(Array.isArray(statsRes?.rows) ? statsRes.rows : []);
    } catch (e) {
      if (e?.response?.status === 403) {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error('Nem sikerült betölteni a promót.');
      }
    } finally { setLoading(false); }
  }, [adminKey, headers]);

  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // -- A/B variants ---------------------------------------------------------
  const variants = form.variants || [];
  const setVariant = (idx, patch) => {
    setForm(prev => {
      const next = [...(prev.variants || [])];
      next[idx] = { ...(next[idx] || {}), ...patch };
      return { ...prev, variants: next };
    });
  };
  const addVariant = () => {
    if (variants.length >= 3) return;
    const key = ['A', 'B', 'C'][variants.length];
    const preset = (PROMO_POPUP_DEFAULT.variants || []).find(v => v.key === key);
    setForm(prev => ({
      ...prev,
      variants: [...(prev.variants || []), preset ? { ...preset } : {
        key,
        enabled: true,
        label: `${key} típus`,
        cta_action: 'booking',
      }],
    }));
    setPreviewVariantKey(key);
  };
  const removeVariant = (idx) => {
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).filter((_, i) => i !== idx),
    }));
    setPreviewVariantKey(null);
  };

  // Merge base + selected variant for the live preview.
  const previewPromo = useMemo(() => {
    if (!previewVariantKey) return form;
    const v = variants.find(x => x.key === previewVariantKey);
    if (!v) return form;
    const overrides = {};
    Object.entries(v).forEach(([k, val]) => {
      if (k === 'key' || k === 'enabled' || k === 'label') return;
      if (val === null || val === undefined || val === '') return;
      overrides[k] = val;
    });
    return { ...form, ...overrides };
  }, [form, previewVariantKey, variants]);

  // Group stats by version and aggregate variants for display.
  const statsByVersion = useMemo(() => {
    const map = new Map();
    stats.forEach(r => {
      const v = r.version || '(nincs)';
      if (!map.has(v)) map.set(v, []);
      map.get(v).push(r);
    });
    return Array.from(map.entries()).map(([version, rows]) => {
      const totalImp = rows.reduce((s, r) => s + (r.impressions || 0), 0);
      const totalClk = rows.reduce((s, r) => s + (r.clicks || 0), 0);
      return {
        version,
        rows: rows.sort((a, b) => (a.variant || '').localeCompare(b.variant || '')),
        totalImp,
        totalClk,
        totalCtr: totalImp > 0 ? totalClk / totalImp : 0,
      };
    });
  }, [stats]);

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields the admin can edit — the backend accepts partial upserts.
      const editable = FIELDS.map(f => f.key).concat(['enabled', 'starts_at', 'ends_at']);
      const payload = {};
      editable.forEach(k => {
        let v = form[k];
        // Schedule fields: send even if empty so the backend clears them.
        if (k === 'starts_at' || k === 'ends_at') {
          payload[k] = v ? String(v) : '';
          return;
        }
        if (v === undefined || v === null || v === '') return;
        if (k === 'cooldown_days' || k === 'delay_ms') v = Number(v);
        payload[k] = v;
      });
      payload.enabled = !!form.enabled;
      // Promo típusok — csak a kitöltött mezők kerülnek mentésre.
      const VARIANT_FIELDS = [
        'label', 'eyebrow', 'headline_line1', 'headline_line2', 'headline_badge',
        'bullet_lead', 'bullet_accent', 'bullet_sub',
        'discount_amount', 'discount_sub', 'discount_fine',
        'cta_label', 'cta_action', 'cta_href',
      ];
      const cleanVariants = (form.variants || [])
        .filter(v => v && v.key)
        .map(v => {
          const out = { key: String(v.key).slice(0, 16), enabled: v.enabled !== false };
          VARIANT_FIELDS.forEach(f => {
            if (v[f] !== undefined && v[f] !== null && v[f] !== '') out[f] = v[f];
          });
          return out;
        });
      payload.variants = cleanVariants;
      await axios.put(`${API_BASE}/api/admin/promo-popup`, payload, { headers });
      toast.success('Promó elmentve.');
    } catch (e) {
      toast.error('Mentés sikertelen.');
    } finally { setSaving(false); }
  };

  const resetToDefault = () => {
    setForm({ ...PROMO_POPUP_DEFAULT, variants: (PROMO_POPUP_DEFAULT.variants || []).map(v => ({ ...v })) });
    setPreviewVariantKey(null);
    toast.info('Alapértelmezett tartalom betöltve (nem mentve).');
  };

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="admin-promo-page">
      <header className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
              <ArrowLeft size={16} /> Vissza az admin dashboardra
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={load}
              className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800"
              data-testid="admin-promo-refresh"
            >
              <RefreshCw size={16} className="mr-2" /> Újratöltés
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
              data-testid="admin-promo-save"
            >
              {saving ? <><Loader2 className="mr-2 animate-spin" size={16} /> Mentés…</> : <><Save size={16} className="mr-2" /> Mentés</>}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-yellow-600/20 flex items-center justify-center">
            <Megaphone className="text-yellow-500" size={20} />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Promó felugró ablak
          </h1>
        </div>
        <p className="text-sm text-gray-400 mb-6 max-w-3xl">
          Ez a felugró ablak {Math.round((form.delay_ms ?? 15000) / 1000)} másodperccel a betöltés után — vagy ha a látogató
          addig legörget — jelenik meg. Ha bezárja, {form.cooldown_days ?? 1} napig nem látja újra. Az <code className="text-yellow-400">/alapozo</code>{' '}
          oldalon egyáltalán nem jelenik meg. Új akció esetén cseréld a szöveget, és ha azt akarod, hogy azok is újra
          lássák, akik bezárták — <strong>bumpold a Verziót</strong>.
        </p>

        <AnnouncementBarEditor adminKey={adminKey} />

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form (3/5) */}
          <div className="lg:col-span-3 space-y-5">
            {/* Statistics — impressions + clicks per version/variant */}
            {statsByVersion.length > 0 && (
              <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4" data-testid="admin-promo-stats">
                <div className="font-bold text-white flex items-center gap-2 mb-3">
                  <BarChart3 size={16} /> Impresszió + kattintás statisztika
                </div>
                {statsByVersion.map(({ version, rows, totalImp, totalClk, totalCtr }) => {
                  // Highlight the best-performing variant in this version.
                  const bestKey = (() => {
                    if (rows.length < 2) return null;
                    let best = rows[0];
                    rows.forEach(r => { if ((r.ctr || 0) > (best.ctr || 0)) best = r; });
                    return best?.variant || best?.variant === null ? best.variant : null;
                  })();
                  return (
                    <div key={version} className="mb-4 last:mb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold text-yellow-400 uppercase tracking-wide truncate">{version}</div>
                        <div className="text-[11px] text-gray-400">
                          Összesen: <span className="text-white font-bold">{totalImp}</span> megjelenés,{' '}
                          <span className="text-white font-bold">{totalClk}</span> kattintás
                          {' · CTR '}<span className="text-white font-bold">{(totalCtr * 100).toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {rows.map((r, i) => {
                          const label = r.variant ? `${r.variant} variáns` : 'Alap (nincs A/B)';
                          const isWinner = rows.length > 1 && r.variant === bestKey && (r.impressions || 0) > 0;
                          return (
                            <div
                              key={`${version}-${r.variant || 'base'}-${i}`}
                              data-testid={`admin-promo-stat-row-${r.variant || 'base'}`}
                              className={`rounded-lg p-3 border ${isWinner ? 'border-emerald-500/60 bg-emerald-500/5' : 'border-gray-700 bg-gray-900/60'}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className={`text-sm font-bold ${isWinner ? 'text-emerald-400' : 'text-white'}`}>
                                  {label}{isWinner && ' 🏆'}
                                </div>
                                <div className={`text-lg font-black ${isWinner ? 'text-emerald-400' : 'text-yellow-400'}`}>
                                  {((r.ctr || 0) * 100).toFixed(1)}%
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-[11px] text-gray-400">
                                <span>Megjelenés: <span className="text-white font-bold">{r.impressions || 0}</span></span>
                                <span>Kattintás: <span className="text-white font-bold">{r.clicks || 0}</span></span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                  CTR = kattintás / megjelenés. A több adat mellett megbízhatóbb: legalább ~100 megjelenés kell értékelhető eredményhez.
                </p>
              </div>
            )}

            {/* Enabled toggle */}
            <div className="flex items-center justify-between bg-gray-800/60 border border-gray-700 rounded-lg p-4">
              <div>
                <div className="font-bold text-white flex items-center gap-2">
                  <Eye size={16} /> Aktív a felugró ablak?
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Kikapcsolva senki nem látja a promót, de a mentések megmaradnak.
                </div>
              </div>
              <Switch
                data-testid="admin-promo-enabled"
                checked={!!form.enabled}
                onCheckedChange={(v) => setField('enabled', v)}
              />
            </div>

            {/* Schedule (from-until) */}
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="font-bold text-white flex items-center gap-2">
                  <CalendarClock size={16} /> Ütemezés (opcionális)
                </div>
                <StatusPill status={status} />
              </div>
              <p className="text-xs text-gray-400 -mt-1">
                Add meg, hogy mikortól és/vagy meddig legyen élő a promó. Üresen hagyva azonnal él,
                amíg ki nem kapcsolod. Csak a főoldali látogatókra érvényes — az admin előnézet mindig látszik.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="f-starts_at" className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">
                    Mettől
                  </Label>
                  <Input
                    id="f-starts_at"
                    data-testid="admin-promo-field-starts_at"
                    type="datetime-local"
                    value={form.starts_at ? String(form.starts_at).slice(0, 16) : ''}
                    onChange={(e) => setField('starts_at', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="f-ends_at" className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">
                    Meddig
                  </Label>
                  <Input
                    id="f-ends_at"
                    data-testid="admin-promo-field-ends_at"
                    type="datetime-local"
                    value={form.ends_at ? String(form.ends_at).slice(0, 16) : ''}
                    onChange={(e) => setField('ends_at', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
              </div>
              {(form.starts_at || form.ends_at) && (
                <button
                  type="button"
                  onClick={() => { setField('starts_at', ''); setField('ends_at', ''); }}
                  className="text-xs text-yellow-500 hover:text-yellow-400 underline decoration-yellow-600/40"
                  data-testid="admin-promo-schedule-clear"
                >
                  Ütemezés törlése
                </button>
              )}
            </div>

            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`f-${f.key}`} className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">
                  {f.label}
                </Label>
                {f.type === 'textarea' ? (
                  <Textarea
                    id={`f-${f.key}`}
                    data-testid={`admin-promo-field-${f.key}`}
                    value={form[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="bg-gray-900 border-gray-700 text-white min-h-[70px]"
                  />
                ) : (
                  <Input
                    id={`f-${f.key}`}
                    data-testid={`admin-promo-field-${f.key}`}
                    type={f.type}
                    min={f.min}
                    max={f.max}
                    value={form[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                )}
              </div>
            ))}

            {/* Promo típusok — max 3 (A konzultáció / B PDF / C minta étrend).
                A bekapcsolt típusok közül a látogató véletlenszerűen egyet lát,
                és visszatérésre is ugyanazt (sticky). */}
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 space-y-3" data-testid="admin-promo-variants">
              <div className="flex items-center justify-between gap-3">
                <div className="font-bold text-white flex items-center gap-2">
                  <BarChart3 size={16} /> Promo típusok
                </div>
                {variants.length < 3 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addVariant}
                    className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800 h-7 text-xs"
                    data-testid="admin-promo-add-variant"
                  >
                    <Plus size={12} className="mr-1" /> Típus hozzáadása
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-400 -mt-1 leading-relaxed">
                Max 3 típus futhat egyszerre. Kapcsold ki azt, amit nem akarsz futtatni — a bekapcsoltak
                közül a látogató véletlenszerűen egyet lát (visszatérésre ugyanazt). Ha mindet kikapcsolod,
                az alap tartalom jelenik meg. A statisztika fentebb mutatja, melyik hoz több kattintást.
              </p>
              {variants.length === 0 && (
                <p className="text-[11px] text-gray-500 italic">
                  Nincs típus — mindenki az alap tartalmat látja.
                </p>
              )}
              {variants.map((v, idx) => (
                <div key={idx} className="rounded-lg border border-gray-700 bg-gray-900/60 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-bold text-yellow-400">
                      {v.key} · {v.label || 'névtelen típus'}
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={v.enabled !== false}
                          onChange={(e) => setVariant(idx, { enabled: e.target.checked })}
                          data-testid={`admin-promo-variant-${v.key}-enabled`}
                          className="accent-yellow-500"
                        />
                        Fut
                      </label>
                      <button
                        type="button"
                        onClick={() => removeVariant(idx)}
                        className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                        data-testid={`admin-promo-remove-variant-${v.key}`}
                      >
                        <Trash2 size={12} /> Törlés
                      </button>
                    </div>
                  </div>
                  {[
                    { k: 'label', label: 'Típus neve (csak neked)' },
                    { k: 'eyebrow', label: 'Felső kisbetűs sor' },
                    { k: 'headline_line1', label: 'Nagy cím — 1. sor' },
                    { k: 'headline_line2', label: 'Nagy cím — 2. sor' },
                    { k: 'headline_badge', label: 'Arany badge' },
                    { k: 'bullet_lead', label: 'Pipás sor — eleje' },
                    { k: 'bullet_accent', label: 'Pipás sor — arany rész' },
                    { k: 'bullet_sub', label: 'Pipás sor alatti magyarázat' },
                    { k: 'discount_amount', label: 'Kiemelt összeg' },
                    { k: 'discount_sub', label: 'Összeg magyarázata' },
                    { k: 'discount_fine', label: 'Apró betű' },
                    { k: 'cta_label', label: 'CTA felirata' },
                  ].map(({ k, label }) => (
                    <div key={k}>
                      <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">{label}</Label>
                      <Input
                        value={v[k] ?? ''}
                        onChange={(e) => setVariant(idx, { [k]: e.target.value })}
                        placeholder={form[k] || ''}
                        className="bg-gray-950 border-gray-700 text-white h-8 text-sm"
                        data-testid={`admin-promo-variant-${v.key}-${k}`}
                      />
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">CTA működése</Label>
                      <select
                        value={v.cta_action || 'booking'}
                        onChange={(e) => setVariant(idx, { cta_action: e.target.value })}
                        data-testid={`admin-promo-variant-${v.key}-cta_action`}
                        className="w-full h-8 rounded-md bg-gray-950 border border-gray-700 text-white text-sm px-2"
                      >
                        <option value="booking">Foglalási űrlap nyílik</option>
                        <option value="link">Oldalra navigál</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Cél oldal (ha navigál)</Label>
                      <Input
                        value={v.cta_href ?? ''}
                        onChange={(e) => setVariant(idx, { cta_href: e.target.value })}
                        placeholder="/minta-etrend"
                        data-testid={`admin-promo-variant-${v.key}-cta_href`}
                        className="bg-gray-950 border-gray-700 text-white h-8 text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 italic">
                    Csak amit kitöltesz, az írja felül az alap tartalmat. Üres mező = az alap érték látszik.
                  </p>
                </div>
              ))}
            </div>

            <div className="pt-2 flex items-center gap-3">
              <Button
                variant="outline"
                onClick={resetToDefault}
                className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800"
                data-testid="admin-promo-reset-default"
              >
                Alapértelmezett szöveg betöltése
              </Button>
            </div>
          </div>

          {/* Live preview (2/5) */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-2 font-bold flex items-center gap-2">
                <Eye size={12} /> Élő előnézet
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-gray-950 to-gray-800 p-4 border border-gray-800" data-testid="admin-promo-preview">
                <PromoPopupCard promo={previewPromo} onCtaClick={() => toast.info('Előnézet — a CTA élesben a foglalás modalt nyitja meg.')} onClose={null} embedded />
              </div>
              {variants.length > 0 && (
                <div className="mt-3 flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 font-bold mr-1">Előnézet:</span>
                  <button
                    type="button"
                    onClick={() => setPreviewVariantKey(null)}
                    className={`text-[11px] px-2 py-0.5 rounded ${previewVariantKey === null ? 'bg-yellow-500 text-gray-900 font-bold' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                    data-testid="admin-promo-preview-variant-base"
                  >
                    Alap
                  </button>
                  {variants.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setPreviewVariantKey(v.key)}
                      className={`text-[11px] px-2 py-0.5 rounded ${previewVariantKey === v.key ? 'bg-yellow-500 text-gray-900 font-bold' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                      data-testid={`admin-promo-preview-variant-${v.key}`}
                    >
                      {v.label || `${v.key} típus`}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                Az előnézet a form állapotát tükrözi (nem menteni!). Élesben ez az ablak {form.delay_ms ?? 1500} ms után jelenik meg a főoldalon.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPromoPopupPage;
